import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import { router } from "@inertiajs/react"

// The user-facing note composer used on the v2 deal show
// page (desktop inline card + mobile bottom sheet). The
// composer handles content + visibility state, `@`/`#`
// autocomplete popovers, the character counter, and
// submission via the existing `notes#create` Inertia
// endpoint.
//
// Optimistic timeline updates are deliberately out of
// scope for this iteration — the ActionCable
// `deal.note.created` broadcast triggers a partial
// `timeline` reload on the deal show page, so the new
// note lands in the feed via the server. TODO: layer an
// optimistic insert once the wire shape stabilises.

const MAX_CHARS = 200
const WARN_AT = 180

export interface NoteComposerEmployee {
  id: number
  name: string
  initials: string | null
  avatarColor?: string | null
}

export interface NoteComposerMentionResult {
  id: number
  name: string
  initials: string | null
}

export interface NoteComposerTagResult {
  tag: string
}

export interface NoteComposerProps {
  deal: { id: number; name: string }
  currentEmployee: NoteComposerEmployee
  onSaved?: () => void
  onCancel?: () => void
  // Mobile sheet vs desktop inline card. Drives layout
  // chrome (padding, position of the visibility chip)
  // only — the form logic is identical.
  variant?: "inline" | "sheet"
  // Lets the parent override the autoFocus behaviour;
  // the sheet wants focus immediately, the inline card
  // only after the user clicks "+ Note".
  autoFocus?: boolean
}

type Popover =
  | { kind: "mention"; query: string; tokenStart: number }
  | { kind: "tag"; query: string; tokenStart: number }
  | null

export function NoteComposer({
  deal,
  currentEmployee,
  onSaved,
  onCancel,
  variant = "inline",
  autoFocus = false,
}: NoteComposerProps) {
  const [content, setContent] = useState("")
  const [visibility, setVisibility] = useState<"team" | "personal">(
    "team",
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [popover, setPopover] = useState<Popover>(null)
  const [mentions, setMentions] = useState<NoteComposerMentionResult[]>(
    [],
  )
  const [tags, setTags] = useState<NoteComposerTagResult[]>([])
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus()
    }
  }, [autoFocus])

  const charCount = content.length
  const overLimit = charCount > MAX_CHARS
  const disabled =
    submitting || content.trim() === "" || overLimit

  const { t } = useLingui()
  const counterClass = useMemo(() => {
    if (overLimit) return "text-destructive font-medium"
    if (charCount >= WARN_AT) return "text-foreground"
    return "text-muted-foreground"
  }, [overLimit, charCount])

  // ──── Autocomplete: derive token under caret ────
  const onContentChange = (next: string) => {
    setContent(next)
    setError(null)
    const caret = textareaRef.current?.selectionStart ?? next.length
    const popoverState = derivePopover(next, caret)
    setPopover(popoverState)
  }

  // Fetch suggestions when popover query changes.
  useEffect(() => {
    if (!popover) {
      setMentions([])
      setTags([])
      return
    }

    const controller = new AbortController()
    const url =
      popover.kind === "mention"
        ? `/v2/deals/${deal.id}/notes/mention_searches?q=${encodeURIComponent(popover.query)}`
        : `/v2/deals/${deal.id}/notes/tag_searches?q=${encodeURIComponent(popover.query)}`

    fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (popover.kind === "mention") {
          setMentions(Array.isArray(data) ? data : [])
        } else {
          setTags(Array.isArray(data) ? data : [])
        }
      })
      .catch(() => {
        // Network errors get silently swallowed — the
        // popover just stays empty. The composer remains
        // usable; mentions become plain text on submit
        // and the server-side parser is the safety net.
      })

    return () => controller.abort()
  }, [deal.id, popover])

  const insertCompletion = (insert: string) => {
    if (!popover) return
    const before = content.slice(0, popover.tokenStart)
    const after = content.slice(
      textareaRef.current?.selectionStart ?? content.length,
    )
    const next = `${before}${insert} ${after}`
    setContent(next)
    setPopover(null)
    // Restore caret after the inserted token
    const caret = before.length + insert.length + 1
    requestAnimationFrame(() => {
      const ta = textareaRef.current
      if (ta) {
        ta.focus()
        ta.setSelectionRange(caret, caret)
      }
    })
  }

  const cancel = useCallback(() => {
    setContent("")
    setError(null)
    setPopover(null)
    setSubmitting(false)
    onCancel?.()
  }, [onCancel])

  const submit = () => {
    if (disabled) return

    setSubmitting(true)
    setError(null)

    // The composer ships a single-paragraph `<p>` body so
    // the existing `MentionParser` / `TagParser` can find
    // tokens. Rich text (bullets, bold) is a follow-up.
    const contentHtml = wrapInParagraph(content)

    router.post(
      `/v2/deals/${deal.id}/notes`,
      {
        note: {
          content_html: contentHtml,
          visibility,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setSubmitting(false)
          setContent("")
          setPopover(null)
          onSaved?.()
        },
        onError: (errors) => {
          setSubmitting(false)
          const message =
            (errors as Record<string, string>).contentHtml ??
            (errors as Record<string, string>).base ??
            t`Couldn't save the note. Try again.`
          setError(message)
        },
      },
    )
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // ⌘+Enter (Mac) / Ctrl+Enter saves
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submit()
      return
    }
    // Escape cancels
    if (e.key === "Escape") {
      e.preventDefault()
      cancel()
      return
    }
    // Up/Down + Enter pick first popover suggestion
    if (popover && e.key === "Enter") {
      e.preventDefault()
      if (popover.kind === "mention" && mentions[0]) {
        insertCompletion(`@${mentions[0].name}`)
      } else if (popover.kind === "tag" && tags[0]) {
        insertCompletion(`#${tags[0].tag}`)
      }
    }
  }

  return (
    <div
      data-testid="note-composer"
      className={
        variant === "inline"
          ? "flex flex-col rounded-xl bg-card border border-border p-3.5 gap-3 focus-within:ring-2 focus-within:ring-brand/15 focus-within:ring-offset-1 focus-within:ring-offset-background"
          : "flex flex-col gap-3"
      }
    >
      {variant === "inline" ? (
        <ComposerHeader currentEmployee={currentEmployee} />
      ) : null}

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label={t`Add a note`}
          placeholder={t`Add a note about this deal…`}
          rows={variant === "inline" ? 4 : 8}
          disabled={submitting}
          className={
            variant === "inline"
              ? "w-full bg-transparent text-foreground text-[14px] leading-[19px] resize-none placeholder:text-muted-foreground focus:outline-none"
              : "w-full bg-transparent text-foreground text-[15px] leading-[22px] resize-none placeholder:text-muted-foreground focus:outline-none"
          }
        />
        {popover ? (
          <AutocompletePopover
            popover={popover}
            mentions={mentions}
            tags={tags}
            onPick={(value) => insertCompletion(value)}
          />
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="text-destructive text-xs leading-4">
          {error}
        </p>
      ) : null}

      {variant === "inline" ? (
        <ComposerFooter
          charCount={charCount}
          counterClass={counterClass}
          disabled={disabled}
          submitting={submitting}
          onCancel={cancel}
          onSave={submit}
        />
      ) : (
        <SheetFooter
          charCount={charCount}
          counterClass={counterClass}
          visibility={visibility}
          onVisibilityChange={setVisibility}
        />
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────
// Subcomponents

function ComposerHeader({
  currentEmployee,
}: {
  currentEmployee: NoteComposerEmployee
}) {
  return (
    <div className="flex items-center gap-2">
      <EmployeeAvatar employee={currentEmployee} />
      <span className="grow font-medium text-foreground text-[13px] leading-4">
        <Trans>Add a note</Trans>
      </span>
      <span className="text-muted-foreground text-[12px] leading-4">
        <Trans>Visible to team</Trans>
      </span>
    </div>
  )
}

function ComposerFooter({
  charCount,
  counterClass,
  disabled,
  submitting,
  onCancel,
  onSave,
}: {
  charCount: number
  counterClass: string
  disabled: boolean
  submitting: boolean
  onCancel: () => void
  onSave: () => void
}) {
  return (
    <div className="flex items-center gap-2">
      <ToolbarIcons />
      <div className="grow" />
      <span className={`tabular-nums text-[11px] leading-4 ${counterClass}`}>
        {charCount}/{MAX_CHARS}
      </span>
      <span className="text-muted-foreground text-[11px] leading-4">
        ⌘+Enter
      </span>
      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        className="rounded-full bg-transparent border border-border text-foreground text-[12px] leading-4 px-3 py-1.5 font-medium hover:bg-muted disabled:opacity-60"
      >
        <Trans>Cancel</Trans>
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={disabled}
        data-testid="note-composer-save"
        className="rounded-full bg-primary text-primary-foreground text-[12px] leading-4 px-3 py-1.5 font-semibold disabled:opacity-60"
      >
        <Trans>Save note</Trans>
      </button>
    </div>
  )
}

function SheetFooter({
  charCount,
  counterClass,
  visibility,
  onVisibilityChange,
}: {
  charCount: number
  counterClass: string
  visibility: "team" | "personal"
  onVisibilityChange: (next: "team" | "personal") => void
}) {
  const { t } = useLingui()
  return (
    <div className="flex items-center gap-3">
      <ToolbarIcons />
      <div className="grow" />
      <button
        type="button"
        onClick={() =>
          onVisibilityChange(visibility === "team" ? "personal" : "team")
        }
        aria-label={t`Toggle note visibility`}
        className="rounded-full border border-border bg-card text-foreground text-[12px] leading-4 px-2.5 py-1 font-medium"
      >
        {visibility === "team" ? t`Visible to team` : t`Personal note`} ▾
      </button>
      <span className={`tabular-nums text-[12px] leading-4 ${counterClass}`}>
        {charCount}/{MAX_CHARS}
      </span>
    </div>
  )
}

function ToolbarIcons() {
  // The icon row is intentionally inert in this iteration —
  // attachments + list / formatting are future work. The
  // icons are rendered so the design lands intact; each
  // button is `aria-label`'d and disabled.
  const { t } = useLingui()
  return (
    <div className="flex items-center gap-1">
      <ToolbarIconButton label={t`Attach file`}>
        <PaperclipIcon />
      </ToolbarIconButton>
      <ToolbarIconButton label={t`Mention someone`}>@</ToolbarIconButton>
      <ToolbarIconButton label={t`Tag`}>#</ToolbarIconButton>
      <ToolbarIconButton label={t`Bulleted list`}>
        <ListIcon />
      </ToolbarIconButton>
    </div>
  )
}

function ToolbarIconButton({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  const { t } = useLingui()
  return (
    <button
      type="button"
      disabled
      aria-label={t`${label} (coming soon)`}
      className="flex items-center justify-center size-7 rounded-md text-muted-foreground text-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  )
}

function EmployeeAvatar({
  employee,
}: {
  employee: NoteComposerEmployee
}) {
  const color = employee.avatarColor ?? "#1E1B17"
  return (
    <div
      className="flex items-center justify-center shrink-0 rounded-full size-6"
      style={{ backgroundColor: color }}
    >
      <span className="font-semibold text-[#F0EDE6] text-[10px] leading-3">
        {employee.initials ?? employee.name.charAt(0)}
      </span>
    </div>
  )
}

function AutocompletePopover({
  popover,
  mentions,
  tags,
  onPick,
}: {
  popover: NonNullable<Popover>
  mentions: NoteComposerMentionResult[]
  tags: NoteComposerTagResult[]
  onPick: (insert: string) => void
}) {
  const { t } = useLingui()
  const items = popover.kind === "mention" ? mentions : tags
  if (items.length === 0) return null

  return (
    <div
      role="listbox"
      aria-label={
        popover.kind === "mention"
          ? t`Mention suggestions`
          : t`Tag suggestions`
      }
      data-testid="note-composer-popover"
      className="absolute left-0 right-0 top-full mt-1 z-10 max-h-48 overflow-y-auto rounded-lg bg-popover border border-border shadow-md"
    >
      {popover.kind === "mention"
        ? mentions.map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected="false"
              onClick={() => onPick(`@${m.name}`)}
              className="flex items-center w-full gap-2 px-3 py-2 hover:bg-muted text-left"
            >
              <div className="flex items-center justify-center size-6 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                {m.initials ?? m.name.charAt(0)}
              </div>
              <span className="text-foreground text-[13px] leading-4">
                {m.name}
              </span>
            </button>
          ))
        : tags.map((t) => (
            <button
              key={t.tag}
              type="button"
              role="option"
              aria-selected="false"
              onClick={() => onPick(`#${t.tag}`)}
              className="flex items-center w-full gap-2 px-3 py-2 hover:bg-muted text-left"
            >
              <span className="text-brand text-[13px] leading-4">
                #{t.tag}
              </span>
            </button>
          ))}
    </div>
  )
}

// ──────────────────────────────────────────────────────
// Helpers

// Find the token under the caret if it starts with `@`
// or `#`. Returns null if the caret isn't on such a
// token. The token spans from the trigger char through
// the caret, allowing letters/digits/underscores after
// the trigger.
export function derivePopover(
  content: string,
  caret: number,
): Popover {
  // Walk backwards from caret until whitespace or trigger
  let i = caret - 1
  while (i >= 0 && /[\w-]/.test(content[i])) {
    i -= 1
  }
  if (i < 0) return null
  const trigger = content[i]
  if (trigger !== "@" && trigger !== "#") return null

  // Make sure the trigger is at the start of a word
  // (beginning of content or after whitespace).
  if (i > 0 && /\S/.test(content[i - 1])) return null

  const query = content.slice(i + 1, caret)
  return {
    kind: trigger === "@" ? "mention" : "tag",
    query,
    tokenStart: i,
  }
}

function wrapInParagraph(content: string): string {
  // Multi-paragraph: split on blank lines so the parser
  // sees one `<p>` per logical paragraph. Single-line
  // content collapses to one `<p>`.
  const trimmed = content.trim()
  if (!trimmed) return ""
  const paragraphs = trimmed.split(/\n{2,}/)
  return paragraphs
    .map((p) => `<p>${escapeHtml(p.replace(/\n/g, " "))}</p>`)
    .join("")
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

// SVG glyphs for the toolbar.
function PaperclipIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M11 3.5L4.5 10C3.7 10.8 3.7 12.2 4.5 13C5.3 13.8 6.7 13.8 7.5 13L13 7.5C13.8 6.7 13.8 5.3 13 4.5C12.2 3.7 10.8 3.7 10 4.5L4.5 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ListIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M5 4H13M5 8H13M5 12H13M3 4H3.01M3 8H3.01M3 12H3.01"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
